import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, ShieldCheck, ArrowRight } from 'lucide-react';
import { devError } from '../lib/devLog';
import { useGeo } from '../contexts/GeoContext';
import { addsUkVat } from '../lib/vat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal that collects the buyer's email + name and starts a Stripe Checkout
 * session for an artwork. Price is *never* sent from the client — the
 * backend looks it up from the database to prevent tampering.
 *
 * Use only on artworks where:
 *   • artwork.available === true
 *   • typeof artwork.price === 'number' && artwork.price > 0
 *   • artwork.artistName !== 'Banksy' (enquiry-only)
 * The Buy button in ArtworkDetailPage already gates on these — but the
 * backend re-validates as a defence in depth.
 */
const BuyArtworkModal = ({ artwork, open, onClose, selectedFormat = null }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { isUK } = useGeo();
  const [delivery, setDelivery] = useState(null);
  const deliveryCountry = delivery || (isUK === false ? 'INTL' : 'GB');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!agreedTerms) {
      setError('Please confirm you accept the gallery’s terms before continuing.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          artwork_id: artwork.id,
          buyer_email: email.trim(),
          buyer_name: name.trim() || null,
          origin_url: window.location.origin,
          format_size: selectedFormat ? selectedFormat.size : null,
          delivery_country: deliveryCountry,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Checkout could not be created');
      }
      // Redirect straight to Stripe Checkout.
      window.location.href = data.checkout_url;
    } catch (err) {
      devError('Public checkout failed:', err);
      setError(err.message || 'Something went wrong — please try again.');
      setLoading(false);
    }
  };

  if (!artwork) return null;
  // If a specific size tier was picked, use its price + priceDisplay so the
  // modal reflects exactly what the buyer clicked. Fall back to the artwork
  // base price when the buyer used the generic "Buy now" button.
  const netPrice = Number(selectedFormat?.price ?? artwork?.price) || 0;
  const shippingFee = deliveryCountry === 'GB' ? 35 : 95;
  const vatDue = deliveryCountry === 'GB' && addsUkVat(artwork) ? (netPrice + shippingFee) * 0.2 : 0;
  const checkoutTotal = (netPrice + shippingFee + vatDue).toLocaleString('en-GB', {
    style: 'currency', currency: 'GBP',
  });
  const priceLabel = selectedFormat?.priceDisplay
    || artwork.priceDisplay
    || (artwork.price ? `£${Number(artwork.price).toLocaleString('en-GB')}` : '');

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="buy-artwork-modal-overlay"
        >
          <motion.div
            className="relative bg-white max-w-md w-full p-8"
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="buy-artwork-modal"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 p-1 text-neutral-400 hover:text-black"
              aria-label="Close"
              data-testid="buy-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">
              Secure Checkout · Stripe
            </div>
            <h2 className="text-2xl font-light tracking-tight mb-2">{artwork.title}</h2>
            <p className="text-sm text-neutral-500 mb-1">{artwork.artistName}</p>
            {selectedFormat && (
              <p className="text-xs text-neutral-500 mb-1" data-testid="buy-modal-format-size">
                {selectedFormat.size}
                {selectedFormat.edition ? ` · ${selectedFormat.edition}` : ''}
              </p>
            )}
            {priceLabel && (
              <p className="text-lg font-medium mb-6" data-testid="buy-modal-price">
                {priceLabel}
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="buyer-name" className="block text-[11px] tracking-[0.2em] uppercase text-neutral-500 mb-2">
                  Your name
                </label>
                <input
                  id="buyer-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-neutral-300 focus:border-black focus:outline-none text-sm"
                  data-testid="buy-modal-name"
                />
              </div>

              <div>
                <label htmlFor="buyer-email" className="block text-[11px] tracking-[0.2em] uppercase text-neutral-500 mb-2">
                  Email
                </label>
                <input
                  id="buyer-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 border border-neutral-300 focus:border-black focus:outline-none text-sm"
                  data-testid="buy-modal-email"
                />
              </div>

              {(
                <div>
                  <label htmlFor="buyer-delivery" className="block text-[11px] tracking-[0.2em] uppercase text-neutral-500 mb-2">
                    Delivery to
                  </label>
                  <select
                    id="buyer-delivery"
                    value={deliveryCountry}
                    onChange={(e) => setDelivery(e.target.value)}
                    className="w-full px-4 py-3 border border-neutral-300 focus:border-black focus:outline-none text-sm bg-white"
                    data-testid="buy-modal-delivery"
                  >
                    <option value="GB">United Kingdom</option>
                    <option value="INTL">Outside the UK</option>
                  </select>
                  <p className="text-[11px] text-neutral-500 mt-2" data-testid="buy-modal-delivery-note">
                    {deliveryCountry === 'GB'
                      ? `Tracked & insured UK shipping £35${addsUkVat(artwork) ? ' · UK VAT (20%) added at checkout' : ''}.`
                      : 'Tracked & insured international shipping £95 · No UK VAT. Import duties may apply in your country.'}
                  </p>
                  {netPrice > 0 && (
                    <p className="text-sm font-medium mt-3" data-testid="buy-modal-total">
                      Total at checkout {checkoutTotal}
                      <span className="text-[11px] font-normal text-neutral-500">
                        {' '}(incl. shipping{vatDue ? ' & VAT' : ''})
                      </span>
                    </p>
                  )}
                </div>
              )}

              <label className="flex items-start gap-3 text-xs text-neutral-600 leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                  className="mt-0.5 shrink-0"
                  data-testid="buy-modal-terms"
                />
                <span>
                  I confirm I’ve read and accept Zebra One Gallery’s terms of sale.
                  Payment is processed by Stripe; the gallery will be in touch shortly
                  after to arrange delivery and certificate of authenticity.
                </span>
              </label>

              {error && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-100 px-3 py-2" data-testid="buy-modal-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-black text-white hover:bg-neutral-800 disabled:opacity-60 disabled:cursor-wait text-xs uppercase tracking-wider font-medium"
                data-testid="buy-modal-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Redirecting to Stripe…
                  </>
                ) : (
                  <>
                    Continue to secure checkout
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <p
                className="text-[11px] text-neutral-500 text-center pt-1"
                data-testid="buy-modal-vat-notice"
              >
                Shipping is added at checkout — see our <a href="/shipping" className="underline">shipping policy</a>.
              </p>

              <p className="flex items-center justify-center gap-2 text-[11px] text-neutral-500 pt-1">
                <ShieldCheck className="w-3.5 h-3.5" />
                Payments processed by Stripe · No card details touch our servers
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BuyArtworkModal;
